import { IGetValueArgs } from "@components/smart/FieldInfo";
import { withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { createPath } from "@odata/BindingContext";
import {
    BusinessPartnerEntity,
    DocumentEntity,
    DocumentItemEntity,
    DocumentItemLinkableToGrantViewEntity,
    EntitySetName,
    GrantClaimEntity
} from "@odata/GeneratedEntityTypes";
import { IBankCustomData } from "@pages/banks/bankTransactions/BankTransactions.utils";
import { loadPairAccounts } from "@pages/grant/Grant.utils";
import { IDefinition } from "@pages/PageUtils";
import { getOneFetch } from "@utils/oneFetch";
import i18next from "i18next";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { Sort } from "../../enums";
import { IFilterQuery, TableStorage } from "../../model/TableStorage";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";

export const getDefinitions = (context: IAppContext): IDefinition => {
    const filterFetch = getOneFetch();

    const filterBarDef = [{
        isValueHelp: true,
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        defaultFilters: [
            DocumentItemLinkableToGrantViewEntity.CreditAccount,
            createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.NumberOurs),
            DocumentItemLinkableToGrantViewEntity.DocumentType,
            createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.DateAccountingTransaction),
            createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.BusinessPartner, BusinessPartnerEntity.Name),
            DocumentItemLinkableToGrantViewEntity.Description,
            DocumentItemLinkableToGrantViewEntity.Amount
        ],
        filterDefinition: {
            [createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.DateAccountingTransaction)]: {},
            [DocumentItemLinkableToGrantViewEntity.DocumentType]: {},
            [createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.BusinessPartner, BusinessPartnerEntity.Name)]: {},
            [DocumentItemLinkableToGrantViewEntity.Description]: {},
            [DocumentItemLinkableToGrantViewEntity.Amount]: {},
            ...withDisplayName(DocumentItemLinkableToGrantViewEntity.DocumentType),
            [createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.NumberOurs)]: {},
            [DocumentItemLinkableToGrantViewEntity.CreditAccount]: {
                label: i18next.t("Grants:Account"),
                additionalProperties: [{ id: "/DebitAccount" }],
                filterName: ["/DebitAccount", "/CreditAccount"],
                fieldSettings: {
                    itemsFactory: async (args: IGetValueArgs) => {
                        const set = await loadPairAccounts(args.storage as TableStorage);
                        if (set) {
                            return Array.from(set.keys()).sort((key1: string, key2: string) => {
                                return key1 < key2 ? -1 : 1;
                            }).map(key => ({
                                id: key,
                                label: key
                            }));
                        }

                        return [];
                    }
                }
            },
            [DocumentItemLinkableToGrantViewEntity.DebitAccount]: {}
        }
    }];

    const table: ISplitPageTableDef = {
        id: "GrantPairTable",
        preventStoreVariant: true,
        initialSortBy: [{ id: createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.DateAccountingTransaction), sort: Sort.Desc}],
        additionalProperties: [{ id: createPath(DocumentItemLinkableToGrantViewEntity.DocumentItem, DocumentItemEntity.Id) },
            { id: createPath(DocumentItemLinkableToGrantViewEntity.DocumentItem, DocumentItemEntity.DateCreated) },
            { id: createPath(DocumentItemLinkableToGrantViewEntity.DocumentItem, DocumentItemEntity.Description) },
            { id: createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.Id) },
            { id: createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.NumberOurs) },
            { id: createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.DocumentType) },
            { id: createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.TransactionCurrencyCode) },
            { id: DocumentItemLinkableToGrantViewEntity.GrantClaimTypeCode }
        ],
        filterBarDef,
        filter: ({ storage }): IFilterQuery => {
            const rootStorage = (storage.getCustomData() as IBankCustomData).rootStorage;
            const bc = rootStorage.data.bindingContext;

            const query: string[] = [`${DocumentItemLinkableToGrantViewEntity.GrantClaim} eq null`];
            if (!bc.isNew()) {
                const key = bc.getKey();
                query.push(`${DocumentItemLinkableToGrantViewEntity.GrantClaim}/${GrantClaimEntity.Grant}/Id eq ${key}`);
            }
            return {
                query: `(${query.join(' OR ')})`
            };
        },
        columns: [
            createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.DateAccountingTransaction),
            createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.NumberOurs),
            DocumentItemLinkableToGrantViewEntity.DocumentType,
            createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.BusinessPartner, BusinessPartnerEntity.Name),
            DocumentItemLinkableToGrantViewEntity.Description,
            DocumentItemLinkableToGrantViewEntity.DebitAccount,
            DocumentItemLinkableToGrantViewEntity.CreditAccount,
            DocumentItemLinkableToGrantViewEntity.Amount,
        ],
        columnDefinition: {
            [createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.BusinessPartner, BusinessPartnerEntity.Name)]: {
                label: i18next.t("Grants:Pair.BusinessPartner"),
            },
            [createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.DateAccountingTransaction)]: {
                label: i18next.t("Grants:Pair.DateCreated")
            },
            [DocumentItemLinkableToGrantViewEntity.Description]: {
                label: i18next.t("Grants:Pair.Description")
            },
            [DocumentItemLinkableToGrantViewEntity.CreditAccount]: {},
            [DocumentItemLinkableToGrantViewEntity.DebitAccount]: {},
            [DocumentItemLinkableToGrantViewEntity.Amount]: {},
            [createPath(DocumentItemLinkableToGrantViewEntity.Document, DocumentEntity.NumberOurs)]: {
                label: i18next.t("Grants:Pair.NumberOurs")
            },
            ...withDisplayName(DocumentItemLinkableToGrantViewEntity.DocumentType)
        }
    };

    return {
        entitySet: EntitySetName.DocumentItemsLinkableToGrant,
        table
    };
};